import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Search, MoreHorizontal, Pencil, Trash2, Car, Home, Briefcase, FileText, ArrowUpDown, ArrowUp, ArrowDown, Download, Eye, CheckCircle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quoteTypeLabels = {
  auto: 'Veicolo',
  casa: 'Casa',
  attivita: 'Attività',
  vita: 'Vita',
  salute: 'Salute',
  rc_professionale: 'RC Prof.'
};

const quoteTypeIcons = {
  auto: Car,
  casa: Home,
  attivita: Briefcase,
  vita: FileText,
  salute: FileText,
  rc_professionale: Briefcase
};

const statusLabels = {
  bozza: 'Bozza',
  proposto: 'Proposto',
  attesa_conferma: 'Attesa Conferma',
  convertito: 'Convertito',
};

const statusColors = {
  bozza: 'bg-slate-100 text-slate-700',
  proposto: 'bg-sky-100 text-sky-700',
  attesa_conferma: 'bg-yellow-100 text-yellow-800',
  convertito: 'bg-emerald-100 text-emerald-700',
};

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, bozza: 0, proposto: 0, attesa_conferma: 0, convertito: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Reset a pagina 1 quando cambiano ricerca/filtro/ordinamento
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, sortField, sortDirection]);

  // Fetch paginato (debounced) ogni volta che cambiano ricerca/filtro/ordinamento/pagina
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, sortField, sortDirection, currentPage]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/quotes/stats`);
      setStats(response.data);
    } catch (error) {
      // silenzioso: le card mostreranno gli ultimi valori noti
    }
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', ITEMS_PER_PAGE);
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(`${API}/quotes?${params.toString()}`);
      setQuotes(response.data.items);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Get best option price from quote
  const getQuotePrice = (quote) => {
    if (quote.options && quote.options.length > 0) {
      const prices = quote.options.map(opt => opt.premium || 0);
      return Math.min(...prices);
    }
    return 0;
  };

  // Get asset label based on type
  const getAssetLabel = (quote) => {
    // Prima controlla asset_label dal backend
    if (quote.asset_label) return quote.asset_label;
    
    // Fallback a asset_details se presente
    if (quote.asset_details) {
      const asset = quote.asset_details;
      if (quote.quote_type === 'auto') {
        return asset.targa || `${asset.marca || ''} ${asset.modello || ''}`.trim() || '-';
      } else if (quote.quote_type === 'casa') {
        return asset.indirizzo || '-';
      } else if (quote.quote_type === 'attivita') {
        return asset.ragione_sociale || asset.nome || '-';
      }
    }
    
    // Fallback a vehicle info per preventivi auto vecchi
    if (quote.quote_type === 'auto' && quote.vehicle) {
      return quote.vehicle.plate || `${quote.vehicle.brand || ''} ${quote.vehicle.model || ''}`.trim() || '-';
    }
    
    return '-';
  };

  // Get agency from options
  const getAgency = (quote) => {
    if (quote.options && quote.options.length > 0) {
      return quote.options[0].agent || '-';
    }
    return '-';
  };

  // Get company from options
  const getCompany = (quote) => {
    if (quote.options && quote.options.length > 0) {
      return quote.options[0].company || '-';
    }
    return '-';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-emerald-600" /> : 
      <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  const handleConvertToPolicy = async (quote) => {
    if (quote.status === 'convertito') {
      toast.error('Preventivo già convertito in polizza');
      return;
    }
    try {
      await axios.post(`${API}/quotes/${quote.id}/convert-to-policy`, { option_indices: [0] });
      toast.success('Preventivo convertito in polizza');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conversione');
    }
  };

  const handleDelete = async (quote) => {
    // Controllo speciale per preventivi convertiti
    if (quote.status === 'convertito') {
      const policyInfo = quote.policy_number ? ` (Polizza ${quote.policy_number})` : '';
      const confirmed = window.confirm(
        `⚠️ Attenzione: Questo preventivo è stato convertito in polizza${policyInfo}.\n\n` +
        `Eliminando il preventivo:\n` +
        `• La polizza rimarrà ATTIVA e funzionante\n` +
        `• Il preventivo verrà rimosso dallo storico\n` +
        `• La polizza perderà il riferimento al preventivo originale\n\n` +
        `Sei sicuro di voler procedere?`
      );
      if (!confirmed) return;
    } else {
      if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    }
    
    try {
      await axios.delete(`${API}/quotes/${quote.id}`);
      toast.success('Preventivo eliminato');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleClientClick = (clientId) => {
    navigate(`/clients?open=${clientId}`);
  };

  const handleAssetClick = (quote) => {
    if (quote.asset_id && quote.client_id) {
      navigate(`/clients?open=${quote.client_id}&asset=${quote.asset_id}`);
    }
  };

  // `quotes` \u00e8 gi\u00e0 la pagina corrente (paginazione server-side), `totalItems` il totale filtrato
  const paginatedQuotes = quotes;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const response = await axios.get(`${API}/quotes?${params.toString()}`);
      const allFiltered = response.data;

      const headers = ['Cliente', 'Bene', 'Agenzia', 'Compagnia', 'Prezzo', 'Data', 'Stato'];
      const rows = allFiltered.map(q => [
        q.client_name || '',
        getAssetLabel(q),
        getAgency(q),
        getCompany(q),
        getQuotePrice(q).toFixed(2),
        formatDate(q.created_at),
        statusLabels[q.status] || q.status
      ]);
      const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `preventivi_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Esportazione completata');
    } catch (error) {
      toast.error('Errore durante l\'esportazione');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Preventivi
          </h1>
          <p className="text-slate-600 mt-1">Gestisci i preventivi assicurativi</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-quotes">
          <Download className="w-4 h-4 mr-2" />
          Esporta CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per cliente, bene, compagnia..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-quotes"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="proposto">Proposto</SelectItem>
                <SelectItem value="attesa_conferma">Attesa Conferma</SelectItem>
                <SelectItem value="convertito">Convertito</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Bozze</p>
            <p className="text-2xl font-bold">{stats.bozza}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Proposti</p>
            <p className="text-2xl font-bold">{stats.proposto}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Attesa Conferma</p>
            <p className="text-2xl font-bold">{stats.attesa_conferma}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Convertiti</p>
            <p className="text-2xl font-bold">{stats.convertito}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nessun preventivo trovato
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold min-w-[180px]">Cliente</TableHead>
                    <TableHead className="font-semibold min-w-[220px]">Bene</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Agenzia</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Compagnia</TableHead>
                    <TableHead className="font-semibold min-w-[100px]">
                      <button onClick={() => handleSort('price')} className="flex items-center gap-1 hover:text-emerald-600">
                        Prezzo {getSortIcon('price')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold min-w-[100px]">
                      <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-emerald-600">
                        Data {getSortIcon('created_at')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-center min-w-[100px]">Stato</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedQuotes.map((quote) => {
                    const Icon = quoteTypeIcons[quote.quote_type] || FileText;
                    return (
                      <TableRow key={quote.id} className={`hover:bg-slate-50 ${
                        quote.status === 'attesa_conferma' ? 'bg-yellow-100/70' : 
                        quote.status === 'proposto' ? 'bg-sky-50' : ''
                      }`} data-testid={`quote-row-${quote.id}`}>
                        <TableCell>
                          <button
                            onClick={() => handleClientClick(quote.client_id)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group"
                          >
                            <span className="font-medium text-slate-900 group-hover:text-blue-600">
                              {quote.client_name || '-'}
                            </span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </button>
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          <button
                            onClick={() => handleAssetClick(quote)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full"
                          >
                            <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                              quote.quote_type === 'auto' ? 'bg-blue-100 text-blue-600' :
                              quote.quote_type === 'casa' ? 'bg-green-100 text-green-600' :
                              'bg-purple-100 text-purple-600'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                              {getAssetLabel(quote)}
                            </span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </button>
                        </TableCell>
                        <TableCell className="text-slate-600">{getAgency(quote)}</TableCell>
                        <TableCell className="text-slate-600">{getCompany(quote)}</TableCell>
                        <TableCell className="font-medium text-emerald-700">
                          {formatCurrency(getQuotePrice(quote))}{quote.payment_frequency === 'semestrale' ? '/sem' : '/anno'}
                        </TableCell>
                        <TableCell className="text-slate-600">{formatDate(quote.created_at)}</TableCell>
                        <TableCell className="text-center">
                          {quote.status === 'convertito' ? (
                            <Badge className={statusColors[quote.status]}>
                              {statusLabels[quote.status]}
                            </Badge>
                          ) : (
                            <Select
                              value={quote.status}
                              onValueChange={async (newStatus) => {
                                try {
                                  await axios.patch(`${API}/quotes/${quote.id}/status`, { status: newStatus });
                                  fetchQuotes();
                                  fetchStats();
                                  toast.success('Stato aggiornato');
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
                                }
                              }}
                            >
                              <SelectTrigger className={`h-7 text-xs font-medium border-0 px-2.5 rounded-full w-auto min-w-0 ${statusColors[quote.status] || 'bg-slate-100 text-slate-700'}`} data-testid={`quote-status-select-${quote.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="bozza">Bozza</SelectItem>
                                <SelectItem value="proposto">Proposto</SelectItem>
                                <SelectItem value="attesa_conferma">Attesa Conferma</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`quote-menu-${quote.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleAssetClick(quote)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Dettagli
                              </DropdownMenuItem>
                              {quote.status !== 'convertito' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleConvertToPolicy(quote)}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                    Converti in Polizza
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAssetClick(quote)}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Modifica
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(quote)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} preventivi
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, idx, arr) => {
                          const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsisBefore && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-slate-900' : ''}`}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              
              {totalPages === 1 && totalItems > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItems} preventiv{totalItems === 1 ? 'o' : 'i'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Quotes;
